import {
  Box,
  List,
  ListItemIcon,
  ListItemText,
  Button,
  Divider,
  ListItemButton,
  useTheme,
  Drawer,
  IconButton,
  Typography,
  Collapse,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import Groups2RoundedIcon from '@mui/icons-material/Groups2Rounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PetsIcon from '@mui/icons-material/Pets';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import FactoryIcon from '@mui/icons-material/Factory';
import LockPersonIcon from '@mui/icons-material/LockPerson';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { Link, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { useAuth } from '../../auth';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ContactEmergencyIcon from '@mui/icons-material/ContactEmergency';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import { Book as BookIcon, BookOnlineOutlined } from '@mui/icons-material';
import MapsHomeWorkIcon from '@mui/icons-material/MapsHomeWork';
import AutoAwesomeMosaicIcon from '@mui/icons-material/AutoAwesomeMosaic';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ScheduleIcon from '@mui/icons-material/Schedule';
import HistoryIcon from '@mui/icons-material/History';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ROLES } from '../../../shared/constants/roles';
import type { Role } from '../../../shared/constants/roles';



interface UserRole {
  id: string;
  name: Role;
  description: string;
}

const navItems = [
  {
    label: 'Dashboard',
    icon: <DashboardRoundedIcon fontSize="medium" />,
    to: '/dashboard',
    roles: [ROLES.ADMIN],
  },
  {
    label: 'Eventos',
    icon: <ApartmentRoundedIcon fontSize="medium" />,
    to: '/events',
    roles: [ROLES.ADMIN, ROLES.ORGANIZER],
  },
  {
    label: 'Organizadores',
    icon: <Groups2RoundedIcon fontSize="medium" />,
    to: '/users',
    roles: [ ROLES.ADMIN],
  },

  {
    label: 'Participantes',
    icon: <ContactPhoneIcon fontSize="medium" />,
    to: '/visitors',
    roles: [ROLES.ADMIN, ROLES.ORGANIZER],
  },
  {
    label: 'Check-in',
    icon: <CheckCircleIcon fontSize="medium" />,
    to: '/tickets/checkin',
    roles: [ROLES.ADMIN, ROLES.ORGANIZER], 
  },
  {
    label: 'WhatsApp',
    icon: <WhatsAppIcon fontSize="medium" />,
    to: '/admin/whatsapp',
    roles: [ROLES.ADMIN], 
  },
  {
    label: 'Cupons',
    icon: <LocalOfferIcon fontSize="medium" />,
    to: '/admin/coupons',
    roles: [ROLES.ADMIN], 
  },

];

// Função utilitária para mapear o nome do ícone (string) para o componente React
const iconMap: Record<string, JSX.Element> = {
  DashboardRoundedIcon: <DashboardRoundedIcon fontSize="medium" />,
  ApartmentRoundedIcon: <ApartmentRoundedIcon fontSize="medium" />,
  Groups2RoundedIcon: <Groups2RoundedIcon fontSize="medium" />,
  ContactEmergencyIcon: <ContactEmergencyIcon fontSize="medium" />,
  MeetingRoomIcon: <MeetingRoomIcon fontSize="medium" />,
  SettingsRoundedIcon: <SettingsRoundedIcon fontSize="medium" />,
  DirectionsCarIcon: <DirectionsCarIcon fontSize="medium" />,
  PetsIcon: <PetsIcon fontSize="medium" />,
  LockPersonIcon: <LockPersonIcon fontSize="medium" />,
  FactoryIcon: <FactoryIcon fontSize="medium" />,
  ContactPhoneIcon: <ContactPhoneIcon fontSize="medium" />,
  CalendarMonthIcon: <CalendarMonthIcon fontSize="medium" />,
  AnnouncementIcon: <AnnouncementIcon fontSize="medium" />,
  BookIcon: <BookIcon fontSize="medium" />,
  MapsHomeWorkIcon: <MapsHomeWorkIcon fontSize="medium" />,
  AutoAwesomeMosaicIcon: <AutoAwesomeMosaicIcon fontSize="medium" />,
  BookOnlineOutlined: <BookOnlineOutlined fontSize="medium" />,
  AccessTimeIcon: <AccessTimeIcon fontSize="medium" />,
  ScheduleIcon: <ScheduleIcon fontSize="medium" />,
  HistoryIcon: <HistoryIcon fontSize="medium" />,
};

// Função para obter os itens do menu (customizado ou default)
function useSidebarMenuItems() {

  return navItems;
}

// Componente para renderizar um item do menu
function MenuItemComponent({ item, onClose, theme, location }: {
  item: any;
  onClose?: () => void;
  theme: any;
  location: any;
}) {
  const [expanded, setExpanded] = useState(false);
  const isSelected = location.pathname === item.to ||
    (item.subItems && item.subItems.some((subItem: any) => location.pathname === subItem.to));

  return (
    <>
      <ListItemButton
        component={item.hasSubItems ? 'div' : Link}
        to={item.hasSubItems ? undefined : item.to}
        selected={isSelected}
        onClick={item.hasSubItems ? () => setExpanded(!expanded) : onClose}
        sx={{
          borderRadius: 2,
          my: 0.5,
          minHeight: 44,
          background: isSelected ? '#E3EDFF' : 'transparent',
          color: isSelected ? theme.palette.primary.main : theme.palette.text.primary,
          fontWeight: isSelected ? 700 : 500,
          '& .MuiListItemIcon-root': {
            color: isSelected ? theme.palette.primary.main : theme.palette.text.primary,
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 0, mr: 2, justifyContent: 'center' }}>
          {item.icon}
        </ListItemIcon>
        <ListItemText
          primary={item.label}
          primaryTypographyProps={{
            fontWeight: isSelected ? 700 : 500,
            fontSize: 16,
            color: 'inherit',
          }}
        />
        {item.hasSubItems && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
            sx={{
              color: 'inherit',
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
        )}
      </ListItemButton>

      {/* Subitens */}
      {item.hasSubItems && (
        <Collapse in={expanded}>
          <List sx={{ pl: 2 }}>
            {item.subItems?.map((subItem: any) => (
              <ListItemButton
                key={subItem.to}
                component={Link}
                to={subItem.to}
                selected={location.pathname === subItem.to}
                onClick={onClose}
                sx={{
                  borderRadius: 2,
                  my: 0.5,
                  minHeight: 40,
                  background: location.pathname === subItem.to ? '#E3EDFF' : 'transparent',
                  color: location.pathname === subItem.to ? theme.palette.primary.main : theme.palette.text.primary,
                  fontWeight: location.pathname === subItem.to ? 700 : 500,
                  pl: 4,
                  '& .MuiListItemIcon-root': {
                    color: location.pathname === subItem.to ? theme.palette.primary.main : theme.palette.text.primary,
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 0, mr: 2, justifyContent: 'center' }}>
                  {iconMap[subItem.icon] || <DashboardRoundedIcon fontSize="small" />}
                </ListItemIcon>
                <ListItemText
                  primary={subItem.label}
                  primaryTypographyProps={{
                    fontWeight: location.pathname === subItem.to ? 700 : 500,
                    fontSize: 14,
                    color: 'inherit',
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
}

const SidebarContent = ({
  onClose,
  collapsed,
  onToggleCollapse,
  showCollapseButton,
}: {
  onClose?: () => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
  showCollapseButton?: boolean;
}) => {
  const location = useLocation();
  const theme = useTheme();
  const { logout, user } = useAuth();
  const menuItems = useSidebarMenuItems();

  const hasPermission = (roles: string[]) => {
    if (!user?.role) return false;
    return roles.some((role) => user.role.toLowerCase() === role.toLowerCase());
  };

  return (
    <Box
      width={250}
      bgcolor={theme.palette.background.paper}
      minHeight="100vh"
      boxShadow={3}
      display="flex"
      flexDirection="column"
      sx={{
        borderRight: `1.5px solid ${theme.palette.divider}`,
        position: 'relative',
      }}
    >
      <Box
        p={2}
        display="flex"
        alignItems="center"
        gap={1}
        justifyContent={'flex-start'}
        sx={{ userSelect: 'none', minHeight: 64, position: 'relative' }}
      >
        <img
          src="/assets/logo/Marca d_agua - Aviva-12.png"
          alt="Logo"
          style={{
            width: 55,
            height: 55,
            
          }}
        />
        <Typography
          fontWeight={800}
          fontSize={18}
          color="primary.main"
       
          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', maxWidth: 160 }}
        >
          Gestão De Eventos
        </Typography>
      </Box>
      <Divider sx={{ mb: 1 }} />
      
      <List sx={{ px: 1 }}>
        {menuItems
          .filter((item) => hasPermission(item.roles))
          .map((item) => (
            <MenuItemComponent
              key={item.to}
              item={item}
              onClose={onClose}
              theme={theme}
              location={location}
            />
          ))}
      </List>
      <Box flex={1} />
      <Box px={2} mb={1}>

        <Button
          fullWidth
          variant="contained"
          startIcon={<LogoutRoundedIcon />}
          onClick={logout}
          sx={{
            borderRadius: 2,
            fontWeight: 700,
            fontSize: 16,
            bgcolor: '#FFF0F0',
            color: theme.palette.error.main,
            boxShadow: 'none',
            textTransform: 'none',
            mb: 1,
            py: 1.2,
            '&:hover': { bgcolor: '#FFE5E5' },
          }}
        >
          Sair
        </Button>
      </Box>
      
    </Box>
  );
};

const Sidebar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 1200;

  return (
    <>
      <Box
        sx={{
          display: { xs: 'block', md: 'none' },
          position: 'absolute',
          top: 16,
          left: 16,
          zIndex: 1301,
        }}
      >
        <IconButton onClick={() => setMobileOpen(true)} color="primary">
          <MenuIcon />
        </IconButton>
      </Box>

      <Drawer
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        variant="temporary"
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 250 },
        }}
      >
        <SidebarContent
          onClose={() => setMobileOpen(false)}
          collapsed={false}
          onToggleCollapse={() => { }}
          showCollapseButton={false}
        />
      </Drawer>

      <Box
        sx={{
          width: collapsed ? 72 : 250,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          minHeight: '100vh',
          transition: 'width 0.35s cubic-bezier(.4,0,.2,1)',
        }}
      >
        <SidebarContent
          collapsed={isDesktop ? collapsed : false}
          onToggleCollapse={() => setCollapsed((prev) => !prev)}
          showCollapseButton={isDesktop}
        />
      </Box>
    </>
  );
};

export default Sidebar;